/*
 This file is part of GNU Taler
 (C) 2022-2024, 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import {
  urlPattern,
  useCurrentLocation,
  useBankCoreApiContext,
  useNavigationContext,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { VNode, h } from "preact";
import { useState } from "preact/hooks";

import { assertUnreachable } from "@gnu-taler/taler-util";
import {
  useRefreshSessionBeforeExpires,
  useSessionState,
} from "./hooks/session.js";
import { CashoutListForAccount } from "./pages/account/CashoutListForAccount.js";
import { MerchantIntegration } from "./pages/account/MerchantIntegration.js";
import { ShowAccountDetails } from "./pages/account/ShowAccountDetails.js";
import { UpdateAccountPassword } from "./pages/account/UpdateAccountPassword.js";
import { AccountPage } from "./pages/AccountPage/index.js";
import { AdminHome } from "./pages/admin/AdminHome.js";
import { CreateNewAccount } from "./pages/admin/CreateNewAccount.js";
import { DownloadStats } from "./pages/admin/DownloadStats.js";
import { RemoveAccount } from "./pages/admin/RemoveAccount.js";
import { BankFrame } from "./pages/BankFrame.js";
import { ConversionRateClassDetails } from "./pages/ConversionRateClassDetails.js";
import { LoginForm } from "./pages/LoginForm.js";
import { NewConversionRateClass } from "./pages/NewConversionRateClass.js";
import { PublicHistoriesPage } from "./pages/PublicHistoriesPage.js";
import { ConversionConfig } from "./pages/regional/ConversionConfig.js";
import { CashoutCreatePage } from "./pages/regional/CashoutCreatePage.js";
import { ShowCashoutDetails } from "./pages/regional/ShowCashoutDetails.js";
import { RegistrationPage } from "./pages/RegistrationPage.js";
import { WireTransfer } from "./pages/WireTransfer.js";
import { WithdrawalOperationPage } from "./pages/WithdrawalOperationPage.js";
import { ActiveWithdrawal } from "./pages/ActiveWithdrawal.js";
import { useSettingsContext } from "./context/settings.js";
import {
  AdminNavigation,
  AdminPrimarySection,
} from "./components/AdminNavigation.js";
import { Transactions } from "./components/Transactions/index.js";
import { AccountList } from "./pages/admin/AccountList.js";
import { ConversionClassList } from "./pages/admin/ConversionClassList.js";
import { WalletWithdrawal } from "./pages/WalletWithdrawal.js";
import { CashAcceptor } from "./pages/CashAcceptor.js";

const TALER_SCREEN_ID = 100;

Routing.SCREEN_ID = TALER_SCREEN_ID;
export function Routing(): VNode {
  const session = useSessionState();
  useRefreshSessionBeforeExpires();

  if (session.state.status === "loggedIn") {
    const { isUserAdministrator, username } = session.state;
    return <PrivateRouting username={username} isAdmin={isUserAdministrator} />;
  }
  return (
    <BankFrame publicAccountsUrl={publicPages.publicAccounts.url({})}>
      <PublicRounting />
    </BankFrame>
  );
}

const publicPages = {
  root: urlPattern(/^\/?$/, () => "#/login"),
  login: urlPattern(/^\/login$/, () => "#/login"),
  register: urlPattern(/^\/register$/, () => "#/register"),
  publicAccounts: urlPattern(/^\/public-accounts$/, () => "#/public-accounts"),
  operationDetails: urlPattern<{ wopid: string }>(
    /\/operation\/(?<wopid>[a-zA-Z0-9-]+)/,
    ({ wopid }) => `#/operation/${wopid}`,
  ),
};

function PublicRounting(): VNode {
  const { i18n } = useTranslationContext();
  const location = useCurrentLocation(publicPages);
  const { navigateTo } = useNavigationContext();
  const [registeredUsername, setRegisteredUsername] = useState<string>();

  switch (location.name) {
    case "root":
    case "login": {
      return (
        <LoginForm
          currentUser={registeredUsername}
          onSuccess={() => navigateTo(privatePages.home.url({}))}
          registrationNotice={
            registeredUsername
              ? i18n.str`Your account was created. Sign in to continue.`
              : undefined
          }
          routeRegister={publicPages.register}
        />
      );
    }
    case "publicAccounts": {
      return <PublicHistoriesPage />;
    }
    case "operationDetails": {
      return (
        <WithdrawalOperationPage
          operationId={location.values.wopid}
          onOperationAborted={() => navigateTo(publicPages.login.url({}))}
          routeClose={publicPages.login}
        />
      );
    }
    case "register": {
      return (
        <RegistrationPage
          routeLogin={publicPages.login}
          onAutoLoginSuccess={() => navigateTo(privatePages.home.url({}))}
          onAutoLoginFailure={(username) => {
            setRegisteredUsername(username);
            navigateTo(publicPages.login.url({}));
          }}
        />
      );
    }
    case undefined: {
      return (
        <NotFound
          home={publicPages.login.url({})}
          homeLabel={i18n.str`Sign in`}
        />
      );
    }
    default:
      assertUnreachable(location);
  }
}

const privatePages = {
  root: urlPattern(/^\/?$/, () => "#/account"),
  homeChargeWallet: urlPattern(
    /\/account\/charge-wallet/,
    () => "#/account/charge-wallet",
  ),
  homeCashAcceptor: urlPattern(
    /^\/account\/cash-acceptor$/,
    () => "#/account/cash-acceptor",
  ),
  homeWireTransfer: urlPattern<{
    account?: string;
    subject?: string;
    amount?: string;
  }>(/^\/account\/wire-transfer$/, () => "#/account/wire-transfer"),
  home: urlPattern(/^\/account$/, () => "#/account"),
  transactions: urlPattern(/^\/transactions$/, () => "#/transactions"),
  adminAccounts: urlPattern(/^\/admin\/accounts$/, () => "#/admin/accounts"),
  adminActivity: urlPattern(/^\/admin\/activity$/, () => "#/admin/activity"),
  adminConversion: urlPattern(
    /^\/admin\/conversion$/,
    () => "#/admin/conversion",
  ),
  cashoutCreate: urlPattern(/\/new-cashout/, () => "#/new-cashout"),
  cashoutDetails: urlPattern<{ cid: string }>(
    /\/cashout\/(?<cid>[a-zA-Z0-9]+)/,
    ({ cid }) => `#/cashout/${cid}`,
  ),
  wireTranserCreate: urlPattern<{
    account?: string;
    subject?: string;
    amount?: string;
  }>(
    /^\/wire-transfer\/(?<account>[a-zA-Z0-9._~-]+)$/,
    ({ account, amount, subject }) => {
      const params = new URLSearchParams();
      if (amount) params.set("amount", amount);
      if (subject) params.set("subject", subject);
      const query = params.toString();
      return `#/wire-transfer/${encodeURIComponent(account ?? "")}${query ? `?${query}` : ""}`;
    },
  ),
  publicAccountList: urlPattern(/\/public-accounts/, () => "#/public-accounts"),
  statsDownload: urlPattern(/\/download-stats/, () => "#/download-stats"),
  accountCreate: urlPattern(/\/new-account/, () => "#/new-account"),
  myAccountDelete: urlPattern(
    /\/delete-my-account/,
    () => "#/delete-my-account",
  ),
  myAccountDetails: urlPattern(/\/my-profile/, () => "#/my-profile"),
  myAccountMerchantIntegration: urlPattern(
    /^\/my-merchant-integration$/,
    () => "#/my-merchant-integration",
  ),
  myAccountPassword: urlPattern(/\/my-password/, () => "#/my-password"),
  myAccountCashouts: urlPattern(/\/my-cashouts/, () => "#/my-cashouts"),
  conversionConfig: urlPattern(/\/conversion$/, () => "#/conversion"),
  accountDetails: urlPattern<{ account: string }>(
    /^\/profile\/(?<account>[a-zA-Z0-9._~-]+)\/details$/,
    ({ account }) => `#/profile/${encodeURIComponent(account)}/details`,
  ),
  accountChangePassword: urlPattern<{ account: string }>(
    /^\/profile\/(?<account>[a-zA-Z0-9._~-]+)\/change-password$/,
    ({ account }) => `#/profile/${encodeURIComponent(account)}/change-password`,
  ),
  accountDelete: urlPattern<{ account: string }>(
    /^\/profile\/(?<account>[a-zA-Z0-9._~-]+)\/delete$/,
    ({ account }) => `#/profile/${encodeURIComponent(account)}/delete`,
  ),
  accountCashouts: urlPattern<{ account: string }>(
    /^\/profile\/(?<account>[a-zA-Z0-9._~-]+)\/cashouts$/,
    ({ account }) => `#/profile/${encodeURIComponent(account)}/cashouts`,
  ),
  startOperation: urlPattern<{ wopid: string }>(
    /\/start-operation\/(?<wopid>[a-zA-Z0-9-]+)/,
    ({ wopid }) => `#/start-operation/${wopid}`,
  ),
  operationDetails: urlPattern<{ wopid: string }>(
    /\/operation\/(?<wopid>[a-zA-Z0-9-]+)/,
    ({ wopid }) => `#/operation/${wopid}`,
  ),
  conversionRateClassCreate: urlPattern(
    /\/new-conversion-rate-class/,
    () => "#/new-conversion-rate-class",
  ),
  conversionRateClassDetails: urlPattern<{ classId: string }>(
    /\/conversion-rate-class\/(?<classId>[0-9]+)\/details/,
    ({ classId }) => `#/conversion-rate-class/${classId}/details`,
  ),
};

function PrivateRouting({
  username,
  isAdmin,
}: {
  username: string;
  isAdmin: boolean;
}): VNode {
  const location = useCurrentLocation(privatePages);
  const { config } = useBankCoreApiContext();
  const settings = useSettingsContext();
  const showPublicAccounts = settings.showPublicAccounts ?? false;
  const adminCurrent = getAdminPrimarySection(location.name);
  const openOperationId =
    location.name === "operationDetails" || location.name === "startOperation"
      ? location.values.wopid
      : undefined;
  return (
    <BankFrame
      account={username}
      publicAccountsUrl={privatePages.publicAccountList.url({})}
      navigation={
        isAdmin ? (
          <AdminNavigation
            current={adminCurrent}
            routeDashboard={privatePages.home}
            routeAccounts={privatePages.adminAccounts}
            routeActivity={privatePages.adminActivity}
            routeConversion={privatePages.adminConversion}
            routeMyAccount={privatePages.myAccountDetails}
            showConversion={config.allow_conversion ?? false}
          />
        ) : undefined
      }
    >
      <ActiveWithdrawal
        username={username}
        openOperationId={openOperationId}
        routeOperation={privatePages.startOperation}
        routeClose={privatePages.home}
      >
        <PrivatePageRouting
          username={username}
          isAdmin={isAdmin}
          showPublicAccounts={showPublicAccounts}
        />
      </ActiveWithdrawal>
    </BankFrame>
  );
}

function getAdminPrimarySection(
  route: keyof typeof privatePages | undefined,
): AdminPrimarySection | undefined {
  if (
    route === "adminAccounts" ||
    route === "accountCreate" ||
    route === "accountDetails" ||
    route === "accountChangePassword" ||
    route === "accountDelete" ||
    route === "accountCashouts" ||
    route === "publicAccountList"
  )
    return "accounts";
  if (route === "adminActivity") return "activity";
  if (
    route === "adminConversion" ||
    route === "conversionConfig" ||
    route === "conversionRateClassCreate" ||
    route === "conversionRateClassDetails"
  )
    return "conversion";
  if (
    route === "myAccountDetails" ||
    route === "myAccountMerchantIntegration" ||
    route === "myAccountPassword" ||
    route === "myAccountDelete"
  )
    return "my-account";
  return route === undefined ? undefined : "dashboard";
}

function PrivatePageRouting({
  username,
  isAdmin,
  showPublicAccounts,
}: {
  username: string;
  isAdmin: boolean;
  showPublicAccounts: boolean;
}): VNode {
  const { navigateTo } = useNavigationContext();
  const { i18n } = useTranslationContext();
  const location = useCurrentLocation(privatePages);

  switch (location.name) {
    case "operationDetails": {
      return (
        <WithdrawalOperationPage
          operationId={location.values.wopid}
          onOperationAborted={() => navigateTo(privatePages.home.url({}))}
          onContinueLater={() => navigateTo(privatePages.home.url({}))}
          routeClose={privatePages.home}
        />
      );
    }
    case "startOperation": {
      return (
        <WithdrawalOperationPage
          operationId={location.values.wopid}
          onOperationAborted={() => navigateTo(privatePages.home.url({}))}
          onContinueLater={() => navigateTo(privatePages.home.url({}))}
          routeClose={privatePages.home}
        />
      );
    }
    case "publicAccountList": {
      return <PublicHistoriesPage routeOverview={privatePages.home} />;
    }
    case "statsDownload": {
      return <DownloadStats routeCancel={privatePages.home} />;
    }
    case "accountCreate": {
      return (
        <CreateNewAccount
          routeCancel={privatePages.adminAccounts}
          onCreateSuccess={() => navigateTo(privatePages.adminAccounts.url({}))}
        />
      );
    }
    case "accountDetails": {
      return (
        <ShowAccountDetails
          account={location.values.account}
          onUpdateSuccess={() => navigateTo(privatePages.adminAccounts.url({}))}
          routeMyAccountDelete={privatePages.myAccountDelete}
          routeMyAccountDetails={privatePages.myAccountDetails}
          routeMyAccountMerchantIntegration={
            privatePages.myAccountMerchantIntegration
          }
          routeMyAccountPassword={privatePages.myAccountPassword}
          routeClose={privatePages.adminAccounts}
        />
      );
    }
    case "accountChangePassword": {
      return (
        <UpdateAccountPassword
          account={location.values.account}
          onUpdateSuccess={() => navigateTo(privatePages.adminAccounts.url({}))}
          routeMyAccountDetails={privatePages.myAccountDetails}
          routeMyAccountMerchantIntegration={
            privatePages.myAccountMerchantIntegration
          }
          routeMyAccountPassword={privatePages.myAccountPassword}
          routeClose={privatePages.adminAccounts}
        />
      );
    }
    case "accountDelete": {
      return (
        <RemoveAccount
          account={location.values.account}
          onUpdateSuccess={() => navigateTo(privatePages.adminAccounts.url({}))}
          routeCancel={privatePages.adminAccounts}
        />
      );
    }
    case "accountCashouts": {
      return (
        <CashoutListForAccount
          account={location.values.account}
          routeCashoutDetails={privatePages.cashoutDetails}
          routeBack={privatePages.adminAccounts}
          backLabel={i18n.str`Back to accounts`}
        />
      );
    }
    case "myAccountDelete": {
      return (
        <RemoveAccount
          account={username}
          onUpdateSuccess={() => navigateTo(privatePages.home.url({}))}
          routeCancel={privatePages.home}
        />
      );
    }
    case "myAccountDetails": {
      return (
        <ShowAccountDetails
          account={username}
          onUpdateSuccess={() => navigateTo(privatePages.home.url({}))}
          routeMyAccountDelete={privatePages.myAccountDelete}
          routeMyAccountDetails={privatePages.myAccountDetails}
          routeMyAccountMerchantIntegration={
            privatePages.myAccountMerchantIntegration
          }
          routeMyAccountPassword={privatePages.myAccountPassword}
          routeClose={privatePages.home}
        />
      );
    }
    case "myAccountMerchantIntegration": {
      return (
        <MerchantIntegration
          account={username}
          routeOverview={privatePages.home}
          routeMyAccountDetails={privatePages.myAccountDetails}
          routeMyAccountMerchantIntegration={
            privatePages.myAccountMerchantIntegration
          }
          routeMyAccountPassword={privatePages.myAccountPassword}
        />
      );
    }
    case "myAccountPassword": {
      return (
        <UpdateAccountPassword
          account={username}
          onUpdateSuccess={() => navigateTo(privatePages.home.url({}))}
          routeMyAccountDetails={privatePages.myAccountDetails}
          routeMyAccountMerchantIntegration={
            privatePages.myAccountMerchantIntegration
          }
          routeMyAccountPassword={privatePages.myAccountPassword}
          routeClose={privatePages.home}
        />
      );
    }
    case "myAccountCashouts": {
      return (
        <CashoutListForAccount
          account={username}
          routeCashoutDetails={privatePages.cashoutDetails}
          routeBack={privatePages.home}
          backLabel={i18n.str`Back to overview`}
        />
      );
    }
    case "root":
    case "home": {
      if (isAdmin) {
        return (
          <AdminHome
            routeCreateWireTransfer={privatePages.homeWireTransfer}
            routeDownloadStats={privatePages.statsDownload}
            routeActivity={privatePages.adminActivity}
          />
        );
      }
      return (
        <AccountPage
          account={username}
          routeAccountDetails={privatePages.myAccountDetails}
          routeOperationDetails={privatePages.startOperation}
          routeChargeWallet={privatePages.homeChargeWallet}
          routeCashAcceptor={privatePages.homeCashAcceptor}
          routeWireTransfer={privatePages.homeWireTransfer}
          routeCashout={privatePages.cashoutCreate}
          routeTransactions={privatePages.transactions}
        />
      );
    }
    case "transactions": {
      return (
        <Transactions
          account={username}
          variant="history"
          routeBack={privatePages.home}
        />
      );
    }
    case "adminActivity": {
      return (
        <Transactions
          account="admin"
          variant="history"
          title={i18n.str`Admin account activity`}
        />
      );
    }
    case "adminAccounts": {
      return (
        <AccountList
          routeCreate={privatePages.accountCreate}
          routeRemoveAccount={privatePages.accountDelete}
          routeShowAccount={privatePages.accountDetails}
          routeUpdatePasswordAccount={privatePages.accountChangePassword}
          routePublicAccounts={privatePages.publicAccountList}
          showPublicAccounts={showPublicAccounts}
        />
      );
    }
    case "adminConversion": {
      return (
        <ConversionClassList
          routeCreate={privatePages.conversionRateClassCreate}
          routeShowDetails={privatePages.conversionRateClassDetails}
          routeDefault={privatePages.conversionConfig}
        />
      );
    }
    case "cashoutCreate": {
      return (
        <CashoutCreatePage
          account={username}
          onCashout={() => navigateTo(privatePages.home.url({}))}
          routeClose={privatePages.home}
          routeHistory={privatePages.myAccountCashouts}
        />
      );
    }
    case "cashoutDetails": {
      return (
        <ShowCashoutDetails
          id={location.values.cid}
          routeClose={privatePages.myAccountCashouts}
        />
      );
    }
    case "wireTranserCreate": {
      return (
        <WireTransfer
          toAccount={location.values.account}
          withAmount={location.params.amount?.[0]}
          withSubject={location.params.subject?.[0]}
          routeCancel={privatePages.home}
          onSuccess={() => navigateTo(privatePages.home.url({}))}
        />
      );
    }
    case "homeChargeWallet": {
      return (
        <WalletWithdrawal
          account={username}
          routeCancel={privatePages.home}
          onOperationCreated={(wopid) =>
            navigateTo(privatePages.startOperation.url({ wopid }))
          }
        />
      );
    }
    case "homeCashAcceptor": {
      return (
        <CashAcceptor
          routeCancel={privatePages.home}
          routeOperation={privatePages.startOperation}
        />
      );
    }
    case "conversionConfig": {
      return (
        <ConversionConfig
          routeCancel={privatePages.adminConversion}
          onUpdateSuccess={() => {
            navigateTo(privatePages.adminConversion.url({}));
          }}
        />
      );
    }
    case "homeWireTransfer": {
      return (
        <WireTransfer
          routeCancel={privatePages.home}
          onSuccess={() => navigateTo(privatePages.home.url({}))}
        />
      );
    }
    case "conversionRateClassCreate": {
      return (
        <NewConversionRateClass
          onCreated={(id) =>
            navigateTo(
              privatePages.conversionRateClassDetails.url({
                classId: String(id),
              }),
            )
          }
          routeCancel={privatePages.adminConversion}
        />
      );
    }
    case "conversionRateClassDetails": {
      const id = Number.parseInt(location.values.classId, 10);
      if (Number.isNaN(id)) {
        // Translators: %1$s is the invalid value from the conversion-rate
        // class identifier in the page URL.
        return (
          <div>
            <i18n.Translate>
              Conversion rate class ID “{location.values.classId}” is not a
              number.
            </i18n.Translate>
          </div>
        );
      }
      return (
        <ConversionRateClassDetails
          classId={id}
          routeCancel={privatePages.adminConversion}
          onClassDeleted={() => {
            navigateTo(privatePages.adminConversion.url({}));
          }}
        />
      );
    }
    case undefined: {
      return (
        <NotFound home={privatePages.home.url({})} homeLabel={i18n.str`Home`} />
      );
    }
    default:
      assertUnreachable(location);
  }
}

function NotFound({
  home,
  homeLabel,
}: {
  home: string;
  homeLabel: string;
}): VNode {
  const { i18n } = useTranslationContext();
  return (
    <section class="mx-auto max-w-xl py-12 text-center">
      <h1 class="text-2xl font-bold text-onBackground">
        <i18n.Translate>Page not found</i18n.Translate>
      </h1>
      <p class="mt-3 text-gray-600">
        <i18n.Translate>The requested bank page does not exist.</i18n.Translate>
      </p>
      <a
        class="mt-6 inline-block rounded-md bg-primary px-4 py-2 font-semibold text-onPrimary"
        href={home}
      >
        {homeLabel}
      </a>
    </section>
  );
}
